/**
 * 共同作業者（第 1b 段・2026-10-22 core/handoff-1b）の、画面から切り離した判断（node --test・projectMembers.test.ts）。
 * 画面: pages/MembersPage.tsx（メンバー）・各画面の「持ち主だけ」の出し分け（components/OwnerOnly.tsx）。
 *
 * API の正: apps/server/src/modules/platform/projectMembers.ts（形は docs/API-REFERENCE.md の「共同作業者」）。
 * 何ができるかの正はサーバーの表（apps/server/src/lib/projectAccess.ts の PROJECT_MEMBER_ROUTES）。画面の出し分けはその写しではなく
 *   「押しても 403 owner_only になる所を最初から出さない」ための見た目だけ。projectMembers.test.ts がサーバーの表を読んで、
 *   ナビで隠す画面の口が表に無いこと・ナビに出す画面の口が表にあることを確かめる（片方だけ直して食い違う、を止める）。
 *
 * role が無い（古いサーバー・持ち主だけの頃の応答）は持ち主として扱う = 今までどおり全部出す（隠しすぎて使えない、より安全側ではないが、
 *   その時はサーバーが持ち主しか通さないので、出しても押せる = 食い違わない）。
 *
 * import は型だけ（node --experimental-strip-types でそのまま読める。projectTransfer.ts と同じ決め事）。
 */
import type { MessageKey } from './i18n';

export type ProjectRole = 'owner' | 'member';

/** 持ち主か（role が無ければ持ち主 = 上の WHY） */
export const isOwnerRole = (role: ProjectRole | null | undefined) => role !== 'member';

/**
 * 共同作業者には出さない画面（ナビの to）。サーバーの表で持ち主だけの口しか無い画面:
 *   sign-in（ログインの設定 = 外部 ID の鍵）・mail（差出人は持ち主の名義）・payments（お金）・keys（鍵）・transfer（受け渡し）
 */
export const OWNER_ONLY_SECTIONS = ['sign-in', 'mail', 'payments', 'keys', 'transfer'] as const;

export const isOwnerOnlySection = (to: string) => (OWNER_ONLY_SECTIONS as readonly string[]).includes(to);

/** 役割で絞ったナビ（共同作業者は持ち主だけの画面を出さない）。隠す・見出しの付け替えは projectNav.ts の visibleNav に任せる */
export function navHiddenForRole(role: ProjectRole | null | undefined): (to: string) => boolean {
  return (to) => !isOwnerRole(role) && isOwnerOnlySection(to);
}

export interface MemberView {
  id: string;
  email: string;
  role: 'collaborator';
  status: 'active' | 'invited';
  you: boolean;
  invitedAt: string;
  joinedAt: string | null;
}

/** GET /v1/projects/:projectId/members */
export interface MembersView {
  role: ProjectRole;
  owner: { you: boolean; email: string | null };
  members: MemberView[];
  limit: number;
}

export const membersApiPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/members`;
export const memberApiPath = (projectId: string, memberId: string) => `${membersApiPath(projectId)}/${encodeURIComponent(memberId)}`;
export const leaveApiPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/leave`;

const normalize = (raw: string) => raw.trim().toLowerCase();

/**
 * 招きの入力の状態。WHY 画面で self / exists / full を先に止めるか: サーバーも 400 / 409 で断るが、再認証のダイアログを出した後で断ると本人の手間が無駄になる
 * （projectTransfer.ts の inviteEmailState と同じ考え方）
 */
export type MemberEmailState = 'empty' | 'invalid' | 'self' | 'exists' | 'full' | 'ok';
export function memberEmailState(input: string, ctx: { ownEmail?: string | null; existing: readonly string[]; limit: number }): MemberEmailState {
  const e = normalize(input);
  if (!e) return 'empty';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return 'invalid';
  if (ctx.ownEmail && e === normalize(ctx.ownEmail)) return 'self';
  if (ctx.existing.some((x) => normalize(x) === e)) return 'exists';
  if (ctx.existing.length >= ctx.limit) return 'full';
  return 'ok';
}

/** 状態の文（active = 加わっている / invited = ログイン待ち） */
export const memberStatusKey = (m: Pick<MemberView, 'status'>): MessageKey => (m.status === 'active' ? 'members.status.active' : 'members.status.invited');

/** サーバーのエラーコード → 画面の文（errorText の overrides） */
export const MEMBER_ERRORS = {
  member_exists: 'members.error.exists',
  member_self: 'members.invite.self',
  member_limit: 'members.error.limit',
  member_security_hold: 'members.error.hold',
  owner_only: 'members.ownerOnly.body',
  owner_cannot_leave: 'members.error.ownerCannotLeave'
} as const satisfies Record<string, MessageKey>;
